import type {
	DtmOrthoFilePair,
	DtmOrthoImportSelection,
	DtmOrthoUploadPhase,
	NamedFile,
} from "@/components/AddData/dtm-ortho/dtmOrtho.types";
import {
	importDtmOrthoPair,
	resolveAssignment,
	resolveInitialDistanceUnit,
} from "@/components/AddData/dtm-ortho/dtmOrthoImport";
import { getBaseName } from "@/components/AddData/fileName";
import { useUploadTracker } from "@/composables/useUploadTracker";
import type { DistanceUnit } from "@/models/siteUnit.types";
import {
	handleSingleUploadSuccess,
	notifyError,
} from "@/utils/notificationUtils";
import { type ComputedRef, type Ref, computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

export interface DtmOrthoUploadFacade {
	readonly phase: Readonly<Ref<DtmOrthoUploadPhase>>;
	/** 種別選択ダイアログに渡す組の 2 要素（端末上の File または外部ストレージのノード参照） */
	readonly files: Readonly<Ref<readonly [NamedFile, NamedFile] | null>>;
	/**
	 * 種別選択ダイアログに渡す取込単位の初期値（AC-47）。
	 * store を setup 時に読まないため computed で遅延評価する
	 */
	readonly initialDistanceUnit: ComputedRef<DistanceUnit>;
	/** origin 付きの tiff の組を受け取り、種別選択に進む（AC-5）。組の生成は host（DtmOrthoTypeFlow の親）が行う */
	start: (pair: DtmOrthoFilePair) => void;
	/** 種別と取込単位を確定して直ちに取込を開始する（事前検査なし。AC-34 / C-13） */
	assign: (selection: DtmOrthoImportSelection) => Promise<void>;
}

/**
 * DTM / オルソ取込フローの facade（手動 / Groupware 経路）。
 * client は GeoTIFF を読まず（C-13）、ネットワーク手順は importDtmOrthoPair に任せる。
 * PUT の要否は組の origin で決まり、ここでは分岐しない（GI-11）。
 *
 * @param getSiteId 現場 UUID を返す getter。現場切替に追従するため実行時に解決する
 */
export function useDtmOrthoUpload(
	getSiteId: () => string | string[] | undefined,
): DtmOrthoUploadFacade {
	const store = useStore();
	const { t } = useI18n();
	const notifyContext = { $store: store, $t: t };

	/** 実行時にだけ現場 UUID を解決する（setup 時の値に固定しない） */
	const resolveSiteId = (): string => {
		const siteId = getSiteId();
		if (typeof siteId !== "string" || siteId.length === 0) {
			throw new Error("siteId is not available");
		}
		return siteId;
	};

	const phase = ref<DtmOrthoUploadPhase>("idle");
	const pair = ref<DtmOrthoFilePair | null>(null);
	const files = computed<readonly [NamedFile, NamedFile] | null>(
		() => pair.value?.files ?? null,
	);

	// store は setup 時に読まず、ダイアログを開くときに初めて評価する（AC-47）
	const initialDistanceUnit = computed<DistanceUnit>(() =>
		resolveInitialDistanceUnit(store),
	);

	const start = (input: DtmOrthoFilePair): void => {
		pair.value = input;
		phase.value = "assigning";
	};

	const assign = async (selection: DtmOrthoImportSelection): Promise<void> => {
		phase.value = "uploading";
		store.dispatch("updateIsLoading", true);

		try {
			const siteId = resolveSiteId();
			// 「前へ」「キャンセル」は host がこの facade ごと破棄するため、assign 時に組は必ずある
			if (!pair.value) throw new Error("No tiff pair has been started");
			const assignment = resolveAssignment(pair.value, selection);
			const ids = await importDtmOrthoPair(siteId, {
				assignment,
				distanceUnit: selection.distanceUnit,
			});

			const { trackItem } = useUploadTracker();
			trackItem({ dataType: "pointcloud", id: ids.pointCloudId }, siteId);
			trackItem(
				{ dataType: "drawing2dImage", id: ids.drawing2dImageId },
				siteId,
			);

			// 一覧更新の失敗は取込の失敗ではない（tracker が処理中の行を拾い直す）
			try {
				await Promise.all([
					store.dispatch("get_point_cloud_list", siteId),
					store.dispatch("drawing2dImage/getDrawing2dImageList", siteId),
				]);
			} catch (error: unknown) {
				console.warn(
					"Failed to refresh the lists after DTM/ortho start",
					error,
				);
			}

			handleSingleUploadSuccess(
				notifyContext,
				getBaseName(assignment.terrainFile.name),
			);
			phase.value = "done";
			pair.value = null;
		} catch (error: unknown) {
			phase.value = "failed";
			pair.value = null;
			// レスポンス本文は console に残し、画面には定型文だけを出す
			notifyError(notifyContext, t("UPLOAD_ERROR"));
			console.error("DTM/ortho upload failed:", error);
		} finally {
			store.dispatch("updateIsLoading", false);
		}
	};

	return {
		phase,
		files,
		initialDistanceUnit,
		start,
		assign,
	};
}
